import React, { useState, useMemo } from 'react';
import { formatCurrency } from '../../utils/helpers';

function SalesAnalytics({ sales }) {
  const [timeframe, setTimeframe] = useState('monthly'); // daily, monthly, yearly

  const chartData = useMemo(() => {
    const grouped = sales.reduce((acc, s) => {
      const dateObj = new Date(s.date);
      let key;
      if (timeframe === 'yearly') key = dateObj.getFullYear().toString();
      else if (timeframe === 'monthly') key = `${dateObj.getFullYear()}-${(dateObj.getMonth() + 1).toString().padStart(2, '0')}`;
      else key = dateObj.toLocaleDateString();

      if (!acc[key]) acc[key] = { label: key, total: 0, heads: 0, kilos: 0, sortKey: dateObj.getTime() };
      acc[key].total += s.total_sales;
      acc[key].heads += s.number_of_heads;
      acc[key].kilos += s.kilos;
      if (dateObj.getTime() < acc[key].sortKey) acc[key].sortKey = dateObj.getTime();
      return acc;
    }, {});

    return Object.values(grouped).sort((a, b) => a.sortKey - b.sortKey);
  }, [sales, timeframe]);

  const dataByLocation = useMemo(() => {
    const grouped = sales.reduce((acc, s) => {
      const key = s.location_name || 'Unknown';
      if (!acc[key]) acc[key] = { label: key, total: 0, heads: 0, kilos: 0 };
      acc[key].total += s.total_sales;
      acc[key].heads += s.number_of_heads;
      acc[key].kilos += s.kilos;
      return acc;
    }, {});
    return Object.values(grouped).sort((a, b) => b.total - a.total);
  }, [sales]);

  const stats = useMemo(() => {
    const total = sales.reduce((acc, s) => acc + s.total_sales, 0);
    const heads = sales.reduce((acc, s) => acc + s.number_of_heads, 0);
    const kilos = sales.reduce((acc, s) => acc + s.kilos, 0);
    return { total, heads, kilos };
  }, [sales]);

  const avgKilos = stats.heads > 0 ? (stats.kilos / stats.heads).toFixed(2) : '0.00';

  const maxTotal = Math.max(...chartData.map(d => d.total), 1);
  const maxLocTotal = Math.max(...dataByLocation.map(d => d.total), 1);

  return (
    <div className="form-section" style={{marginBottom: '2rem'}}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem'}}>
        <h3 style={{margin: 0}}>Sales Analytics</h3>
        <div className="button-group" style={{display: 'flex', gap: '0.5rem'}}>
          {['daily', 'monthly', 'yearly'].map(t => (
            <button 
              key={t}
              onClick={() => setTimeframe(t)}
              className="action-btn"
              style={{
                backgroundColor: timeframe === t ? '#34495e' : '#bdc3c7',
                fontSize: '0.8rem',
                padding: '0.25rem 0.75rem'
              }}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem'}}>
        <div style={{padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', textAlign: 'center', border: '1px solid #eee'}}>
          <div style={{fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem'}}>Total Revenue</div>
          <div style={{fontSize: '1.2rem', fontWeight: 'bold', color: '#27ae60'}}>{formatCurrency(stats.total)}</div>
        </div>
        <div style={{padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', textAlign: 'center', border: '1px solid #eee'}}>
          <div style={{fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem'}}>Total Heads</div>
          <div style={{fontSize: '1.2rem', fontWeight: 'bold', color: '#2c3e50'}}>{stats.heads.toLocaleString()}</div>
        </div>
        <div style={{padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', textAlign: 'center', border: '1px solid #eee'}}>
          <div style={{fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem'}}>Total Weight</div>
          <div style={{fontSize: '1.2rem', fontWeight: 'bold', color: '#2c3e50'}}>{stats.kilos.toLocaleString()} kg</div>
        </div>
        <div style={{padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', textAlign: 'center', border: '1px solid #eee'}}>
          <div style={{fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem'}}>Avg kg / Head</div>
          <div style={{fontSize: '1.2rem', fontWeight: 'bold', color: '#2c3e50'}}>{avgKilos}</div>
        </div>
      </div>

      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem'}}>
        <div>
          <h4>Revenue Trend</h4>
          <div style={{height: '200px', display: 'flex', alignItems: 'flex-end', gap: '4px', borderBottom: '2px solid #ddd', paddingBottom: '5px'}}>
            {chartData.map((d, i) => (
              <div 
                key={i} 
                style={{
                  flex: 1, 
                  height: `${(d.total / maxTotal) * 100}%`, 
                  backgroundColor: 'var(--secondary-color)',
                  minWidth: timeframe === 'daily' ? '5px' : '30px',
                  transition: 'height 0.3s ease'
                }} 
                title={`${d.label}: ${formatCurrency(d.total)}`}
              />
            ))}
          </div>
          <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', marginTop: '5px', color: '#666'}}>
            <span>{chartData[0]?.label}</span>
            <span>{chartData[chartData.length - 1]?.label}</span>
          </div>
        </div>

        <div>
          <h4>Revenue by Location</h4>
          <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
            {dataByLocation.map((d, i) => (
              <div key={i}>
                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '4px'}}>
                  <span>{d.label}</span>
                  <strong>{formatCurrency(d.total)}</strong>
                </div>
                <div style={{height: '12px', backgroundColor: '#eee', borderRadius: '6px', overflow: 'hidden'}}>
                  <div style={{height: '100%', width: `${(d.total / maxLocTotal) * 100}%`, backgroundColor: 'var(--secondary-color)'}} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SalesAnalytics;
